import React, { useMemo, useState } from 'react';
import { useToolManual } from '../../hooks/useToolManual';
import './HelpSection.css';
import { matchesSearch } from '../../utils/search';

function toKinescopeEmbed(url) {
    if (!url) return '';
    if (url.includes('/embed/')) return url;
    const m = url.match(/kinescope\.ru\/([a-zA-Z0-9_-]+)/);
    return m ? `https://kinescope.ru/embed/${m[1]}` : url;
}

function VideoModal({ title, url, onClose }) {
    return (
        <div className="hlp-modal__overlay" onClick={onClose}>
            <div className="hlp-modal__box hlp-modal__box--video" onClick={e => e.stopPropagation()}>
                <button className="hlp-modal__close" onClick={onClose}>×</button>
                <div className="hlp-modal__video-wrap">
                    <iframe
                        src={toKinescopeEmbed(url)}
                        title={title}
                        allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                        allowFullScreen
                        frameBorder="0"
                    />
                </div>
            </div>
        </div>
    );
}

function TextModal({ title, text, onClose }) {
    return (
        <div className="hlp-modal__overlay" onClick={onClose}>
            <div className="hlp-modal__box" onClick={e => e.stopPropagation()}>
                <button className="hlp-modal__close" onClick={onClose}>×</button>
                <h3 className="hlp-modal__title">{title}</h3>
                <div className="hlp-modal__text">{text}</div>
            </div>
        </div>
    );
}

function ToolItem({ tool }) {
    const [preview, setPreview] = useState(null);
    const hasVideo = !!tool.video_url;
    const hasText = !!tool.manual_text;
    if (!hasVideo && !hasText) return null;
    return (
        <div className="hlp-tool">
            <span className="hlp-tool__icon">{tool.icon || '🔧'}</span>
            <span className="hlp-tool__title">{tool.title}</span>
            <div className="hlp-tool__actions">
                {hasVideo && (
                    <button className="hlp-tool__btn" onClick={() => setPreview('video')}>▶ Видео</button>
                )}
                {hasText && (
                    <button className="hlp-tool__btn" onClick={() => setPreview('text')}>📄 Инструкция</button>
                )}
            </div>
            {preview === 'video' && <VideoModal title={tool.title} url={tool.video_url} onClose={() => setPreview(null)} />}
            {preview === 'text' && <TextModal title={tool.title} text={tool.manual_text} onClose={() => setPreview(null)} />}
        </div>
    );
}

// Раздел «Добавление проекта и кабинета» приезжает с бэкенда вместе с остальным
// мануалом (ключ секции basics) и редактируется в /admin/tool-manual.
// Разметка текста статьи:
//   обычная строка    — абзац
//   • пункт           — маркированный список
//   1. пункт          — нумерованные шаги
//   | ячейка | ячейка — строка таблицы, первая строка блока = заголовок
//   > текст           — важное замечание
//   [Заголовок] текст — блок-пример
const BASICS_SECTION_KEY = 'basics';

function parseManualBlocks(text) {
    const blocks = [];
    let buf = null;
    const flush = () => { if (buf) { blocks.push(buf); buf = null; } };

    (text || '').split('\n').forEach(raw => {
        const line = raw.trim();
        if (!line) { flush(); return; }

        if (line.startsWith('•')) {
            if (!buf || buf.type !== 'list') { flush(); buf = { type: 'list', items: [] }; }
            buf.items.push(line.replace(/^•\s*/, ''));
            return;
        }
        if (/^\d+[.)]\s/.test(line)) {
            if (!buf || buf.type !== 'steps') { flush(); buf = { type: 'steps', items: [] }; }
            buf.items.push(line.replace(/^\d+[.)]\s*/, ''));
            return;
        }
        if (line.startsWith('|')) {
            const cells = line.split('|').slice(1).map(c => c.trim());
            while (cells.length > 1 && cells[cells.length - 1] === '') cells.pop();
            if (!buf || buf.type !== 'table') { flush(); buf = { type: 'table', head: cells, rows: [] }; }
            else buf.rows.push(cells);
            return;
        }
        flush();
        if (line.startsWith('>')) {
            blocks.push({ type: 'note', text: line.replace(/^>\s*/, '') });
            return;
        }
        const example = line.match(/^\[([^\]]+)\]\s*(.*)$/);
        if (example) blocks.push({ type: 'example', title: example[1], text: example[2] });
        else blocks.push({ type: 'p', text: line });
    });

    flush();
    return blocks;
}


function BasicsBlock({ block }) {
    switch (block.type) {
        case 'p':
            return <p className="hlp-basics__p">{block.text}</p>;
        case 'list':
            return <ul className="hlp-basics__list">{block.items.map((item, i) => <li key={i}>{item}</li>)}</ul>;
        case 'steps':
            return <ol className="hlp-basics__list">{block.items.map((item, i) => <li key={i}>{item}</li>)}</ol>;
        case 'table':
            return (
                <div className="hlp-basics__table-wrap">
                    <table className="hlp-basics__table">
                        <thead><tr>{block.head.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
                        <tbody>{block.rows.map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>)}</tbody>
                    </table>
                </div>
            );
        case 'example':
            return (
                <div className="hlp-basics__example">
                    <div className="hlp-basics__example-title">{block.title}</div>
                    {block.text}
                </div>
            );
        case 'note':
            return <div className="hlp-basics__note">{block.text}</div>;
        default:
            return null;
    }
}

function BasicsArticle({ article, forceOpen }) {
    const [open, setOpen] = useState(false);
    const [video, setVideo] = useState(false);
    const isOpen = open || forceOpen;
    const blocks = useMemo(() => parseManualBlocks(article.manual_text), [article.manual_text]);
    return (
        <div className="hlp-basics__article">
            <div className="hlp-basics__article-head" onClick={() => setOpen(v => !v)}>
                <span className="hlp__expand-icon">{isOpen ? '▼' : '▶'}</span>
                <span>{article.title}</span>
                {article.video_url && (
                    <button
                        className="hlp-tool__btn hlp-basics__video-btn"
                        onClick={e => { e.stopPropagation(); setVideo(true); }}
                    >
                        ▶ Видео
                    </button>
                )}
            </div>
            {isOpen && (
                <div className="hlp-basics__article-body">
                    {blocks.map((block, i) => <BasicsBlock key={i} block={block} />)}
                </div>
            )}
            {video && <VideoModal title={article.title} url={article.video_url} onClose={() => setVideo(false)} />}
        </div>
    );
}

function matchesQuery(tool, q) {
    return matchesSearch(tool, q);
}

export default function HelpSection() {
    const { sections, loading } = useToolManual();
    const [query, setQuery] = useState('');
    const [collapsedSecs, setCollapsedSecs] = useState(new Set());

    const toggleSec = key => setCollapsedSecs(prev => {
        const s = new Set(prev);
        s.has(key) ? s.delete(key) : s.add(key);
        return s;
    });

    const q = query.trim().toLowerCase();

    const basicsSection = useMemo(
        () => sections.find(sec => sec.key === BASICS_SECTION_KEY) || null,
        [sections],
    );

    const filteredSections = useMemo(() => {
        return sections
            .filter(sec => sec.key !== BASICS_SECTION_KEY)
            .map(sec => {
                const modules = (sec.modules || [])
                    .map(mod => ({ ...mod, tools: (mod.tools || []).filter(t => matchesQuery(t, q) && (t.video_url || t.manual_text)) }))
                    .filter(mod => mod.tools.length > 0);
                const directTools = (sec.direct_tools || []).filter(t => matchesQuery(t, q) && (t.video_url || t.manual_text));
                return { ...sec, modules, direct_tools: directTools };
            })
            .filter(sec => sec.modules.length > 0 || sec.direct_tools.length > 0);
    }, [sections, q]);

    const basicsArticles = useMemo(
        () => (basicsSection?.direct_tools || []).filter(a => matchesQuery(a, q)),
        [basicsSection, q],
    );

    return (
        <div className="hlp">
            <div className="hlp__header">
                <h2 className="hlp__title">Справка</h2>
                <input
                    type="text"
                    className="hlp__search"
                    placeholder="Поиск по инструментам…"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                />
            </div>

            {loading && <div className="hlp__loading">Загрузка…</div>}
            <div className="hlp__sections">
                {basicsArticles.length > 0 && (
                    <div className="hlp__section">
                        <div className="hlp__sec-header" onClick={() => toggleSec(BASICS_SECTION_KEY)}>
                            <span className="hlp__expand-icon">{collapsedSecs.has(BASICS_SECTION_KEY) ? '▶' : '▼'}</span>
                            <span className="hlp__sec-icon">{basicsSection?.icon || '🗂'}</span>
                            <span className="hlp__sec-label">{basicsSection?.label || 'Добавление проекта и кабинета'}</span>
                        </div>
                        {!collapsedSecs.has(BASICS_SECTION_KEY) && (
                            <div className="hlp__sec-body">
                                {basicsArticles.map(article => (
                                    <BasicsArticle key={article.key} article={article} forceOpen={!!q} />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {!loading && filteredSections.length === 0 && basicsArticles.length === 0 && (
                <div className="hlp__empty">
                    {q ? 'Ничего не найдено.' : 'Инструкции пока не заполнены.'}
                </div>
            )}

            <div className="hlp__sections hlp__sections--tools">
                {filteredSections.map(sec => (
                    <div className="hlp__section" key={sec.key}>
                        <div className="hlp__sec-header" onClick={() => toggleSec(sec.key)}>
                            <span className="hlp__expand-icon">{collapsedSecs.has(sec.key) ? '▶' : '▼'}</span>
                            <span className="hlp__sec-icon">{sec.icon}</span>
                            <span className="hlp__sec-label">{sec.label}</span>
                        </div>
                        {!collapsedSecs.has(sec.key) && (
                            <div className="hlp__sec-body">
                                {sec.modules.map(mod => (
                                    <div className="hlp__module" key={mod.key}>
                                        <div className="hlp__mod-label">{mod.label}</div>
                                        {mod.tools.map(tool => <ToolItem key={tool.key} tool={tool} />)}
                                    </div>
                                ))}
                                {sec.direct_tools.map(tool => <ToolItem key={tool.key} tool={tool} />)}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
